import { EnumLikeAsConst } from "../../../helper-services/enum-like-as-const";
import { DefaultFlagValue } from "./DefaultFlagValue";

const { None, Disabled, Enabled, FlagDisabled, FlagEnabled } = DefaultFlagValue;

const InvalidDisabled = 0b1010;
const InvalidEnabled = 0b1011;
const InvalidFlagDisabled = 0b1110;
const InvalidFlagEnabled = 0b1111;

const INVALID_CONSTRAINT_BIT_SHIFT = 3;
const INVALID_CONSTRAINT_MASK = 1 << INVALID_CONSTRAINT_BIT_SHIFT; // 0b1000;
type INVALID_CONSTRAINT_MASK = typeof INVALID_CONSTRAINT_MASK;

type ConstraintValueEntries =
  | typeof None
  | typeof Disabled
  | typeof Enabled
  | typeof FlagDisabled
  | typeof FlagEnabled
  | typeof InvalidDisabled
  | typeof InvalidEnabled
  | typeof InvalidFlagDisabled
  | typeof InvalidFlagEnabled;

// handwritten to break circular ref.
export type ConstraintValue =
  | ConstraintValueEntries
  | ((v: ConstraintValueEntries) => boolean)
  | ((v: ConstraintValueEntries) => ConstraintValueEntries);

/**
 * additional states for DefaultFlagValue, set when a constraint apart from value is _not_ met and value is invalid.
 * useful when preserving distinction between a user set and a flag set value while representing of external constraints
 */
export const ConstraintValue = {
  None: None,
  Disabled: Disabled,
  Enabled: Enabled,
  FlagDisabled: FlagDisabled,
  FlagEnabled: FlagEnabled,

  InvalidDisabled: InvalidDisabled,
  InvalidEnabled: InvalidEnabled,
  InvalidFlagDisabled: InvalidFlagDisabled,
  InvalidFlagEnabled: InvalidFlagEnabled,

  is: (v: ConstraintValue): boolean => Object.values(ConstraintValue).includes(v),
  isNone: (v: ConstraintValue): boolean => v === None,
  isEnabled: (v: ConstraintValue): boolean => v === FlagEnabled || v === Enabled,
  isDisabled: (v: ConstraintValue): boolean => v === FlagDisabled || v === Disabled,
  isDefault: (v: ConstraintValue): boolean => v === FlagDisabled || v === FlagEnabled,
  isPristine: (v: ConstraintValue): boolean =>
    v === None || v === FlagDisabled || v === FlagEnabled,
  isTouched: (v: ConstraintValue): boolean => v === Disabled || v === Enabled,

  //
  isValid: (v: ConstraintValue): boolean => {
    let valid =
      v !== ConstraintValue.None &&
      typeof v === "number" &&
      ((v >> INVALID_CONSTRAINT_BIT_SHIFT) & 1) === 0;
    return valid;
  },
  isInvalid: (v: ConstraintValue): boolean => {
    let invalid: boolean =
      v !== ConstraintValue.None &&
      typeof v === "number" &&
      ((v >> INVALID_CONSTRAINT_BIT_SHIFT) & 1) === 1;
    return invalid;
  },

  /**
   *
   * @param v ConstraintValue.X
   * @returns ConstraintValue.InvalidX or ConstraintValue.None
   */
  asInvalid: (v: ConstraintValue): ConstraintValue => {
    if (v === ConstraintValue.None) return v;

    if (typeof v !== "number") return ConstraintValue.None;

    return (v | INVALID_CONSTRAINT_MASK) as ConstraintValue;
  },
  /**
   *
   * @param v ConstraintValue.X
   * @returns ConstraintValue.X without invalid flag
   */
  asValid: (v: ConstraintValue): ConstraintValue => {
    if (v === ConstraintValue.None) return v;

    if (typeof v !== "number") return ConstraintValue.None;

    return (v & ~INVALID_CONSTRAINT_MASK) as ConstraintValue;
  },

  ...EnumLikeAsConst.stub<ConstraintValue>(),
} as const;

EnumLikeAsConst.define(ConstraintValue, "ConstraintValue");
